import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd, updateHeaderSearchOffset } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;

  /** @returns {boolean} */
  #isDesktopSearchDrawer() {
    return this.id === 'search-modal' && window.matchMedia('(min-width: 750px)').matches;
  }

  /** @returns {number} */
  #measureHeaderBottom() {
    const header = document.querySelector('#header-component');

    if (!(header instanceof HTMLElement)) {
      const fallback = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height'));
      return Number.isFinite(fallback) ? Math.round(fallback) : 56;
    }

    const stickyHeader = header.closest('.shopify-section');
    const target = stickyHeader instanceof HTMLElement ? stickyHeader : header;

    return Math.max(0, Math.round(target.getBoundingClientRect().bottom));
  }

  /**
   * @param {HTMLDialogElement} dialog
   * @param {number} top
   */
  #applySearchDrawerLayout(dialog, top) {
    const topPx = `${top}px`;

    document.documentElement.style.setProperty('--header-search-offset', topPx);
    dialog.style.setProperty('--header-search-offset', topPx);
    dialog.style.setProperty('position', 'fixed');
    dialog.style.setProperty('top', topPx);
    dialog.style.setProperty('left', '0');
    dialog.style.setProperty('right', '0');
    dialog.style.setProperty('bottom', 'auto');
    dialog.style.setProperty('inset', 'auto');
    dialog.style.setProperty('margin', '0');
    dialog.style.setProperty('width', '100vw');
    dialog.style.setProperty('max-width', 'none');
    dialog.style.setProperty('max-height', `calc(100dvh - ${top}px)`);
  }

  /** @param {HTMLDialogElement} dialog */
  #clearSearchDrawerLayout(dialog) {
    document.documentElement.style.removeProperty('--header-search-offset');

    dialog.style.removeProperty('--header-search-offset');
    dialog.style.removeProperty('position');
    dialog.style.removeProperty('top');
    dialog.style.removeProperty('left');
    dialog.style.removeProperty('right');
    dialog.style.removeProperty('bottom');
    dialog.style.removeProperty('inset');
    dialog.style.removeProperty('margin');
    dialog.style.removeProperty('width');
    dialog.style.removeProperty('max-width');
    dialog.style.removeProperty('max-height');
  }

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    const scrollY = window.scrollY;
    this.#previousScrollY = scrollY;
    const isDesktopSearchDrawer = this.#isDesktopSearchDrawer();

    // Measure while the sticky header is still in its natural place. Body position:fixed
    // scroll-lock shifts the page and breaks header offset calculations.
    const headerBottom = isDesktopSearchDrawer ? this.#measureHeaderBottom() : null;

    requestAnimationFrame(() => {
      if (isDesktopSearchDrawer) {
        document.documentElement.setAttribute('scroll-lock', '');
      } else {
        document.body.style.width = '100%';
        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollY}px`;
      }

      dialog.showModal();
      this.dispatchEvent(new DialogOpenEvent());

      if (isDesktopSearchDrawer && headerBottom !== null) {
        this.#applySearchDrawerLayout(dialog, headerBottom);
        updateHeaderSearchOffset();
      }

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    // Force browser to restart animation by resetting it
    // Temporarily remove any existing animation state
    dialog.style.animation = 'none';

    // Force a reflow
    void dialog.offsetWidth;

    // Now add the closing class and restore animation
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    const isDesktopSearchDrawer = this.#isDesktopSearchDrawer();

    if (isDesktopSearchDrawer) {
      document.documentElement.removeAttribute('scroll-lock');
      this.#clearSearchDrawerLayout(dialog);
    } else {
      document.body.style.width = '';
      document.body.style.position = '';
      document.body.style.top = '';
      window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
    }

    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;
        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);